import React, { FunctionComponent, KeyboardEvent } from 'react'
import { GestureResponderEvent, Pressable, TextStyle, ViewStyle } from 'react-native'

import { useTheme } from 'styled-components/native'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { IS_WEB } from '../../constants'
import { BodyTextSize, TextAlign } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'

export type LinkProps = {
  text: string | React.ReactNode
  onPress: (e: GestureResponderEvent | KeyboardEvent<Element>) => void
  size?: BodyTextSize
  color?: string
  testID?: string
  style?: TextStyle
  underline?: boolean
  textAlign?: TextAlign
  containerStyle?: object // containerStyle only for web
  medium?: boolean
  bold?: boolean
  accessibilityLabel?: string
  importantForAccessibility?: 'auto' | 'yes' | 'no' | 'no-hide-descendants'
}

/**
 * Component that styles a link
 */
export const Link: FunctionComponent<LinkProps> = ({
  text,
  onPress,
  size = BodyTextSize.DEFAULT,
  testID,
  color,
  underline,
  textAlign,
  containerStyle, // containerStyle only for web
  style,
  medium,
  bold,
  accessibilityLabel,
  importantForAccessibility,
}) => {
  const { colors } = useTheme() as ThemeType
  const webContainerStyle = { display: 'inline', ...containerStyle } as unknown as ViewStyle // Allow inline for web

  const onKeyDown = (event: KeyboardEvent<Element>) => {
    if (event?.key === KEYS.ENTER) {
      onPress(event)
    }
  }

  const textColor = color || colors.linkDefault
  // there's a known bug in React Native where the vertical alignment of text is misaligned
  // if Text is nested inside other Text / View elements
  // https://github.com/facebook/react-native/issues/31955
  // https://stackoverflow.com/questions/66590167/vertically-align-pressable-inside-a-text-component
  // this alignment issue only appears in the app and not on web

  // if we remove the parent Pressable container, the misalignment goes away
  // however this means containerStyle is only applied for web elements,
  // which is fine so far because it's only being used in lyrahealthwebapp/MedsOptionsAdvisory
  // but we may need to add it back in for app later if it becomes necessary
  return IS_WEB ? (
    <Pressable
      accessibilityRole='link'
      accessibilityLabel={accessibilityLabel}
      testID={tID(testID)}
      onPress={onPress}
      onKeyDown={onKeyDown}
      style={webContainerStyle}
      {...(accessibilityLabel && { accessibilityLabel })}
    >
      <BodyText
        size={size}
        color={textColor}
        style={style}
        underline={underline}
        textAlign={textAlign}
        medium={medium}
        bold={bold}
      >
        {text}
      </BodyText>
    </Pressable>
  ) : (
    <BodyText
      accessible
      accessibilityRole='link'
      accessibilityLabel={accessibilityLabel}
      testID={tID(testID)}
      size={size}
      color={textColor}
      style={style}
      underline={underline}
      textAlign={textAlign}
      onPress={onPress}
      medium={medium}
      bold={bold}
      {...(importantForAccessibility && { importantForAccessibility })}
    >
      {text}
    </BodyText>
  )
}
